import type { Editor, KeyboardShortcutCommand } from '@tiptap/core';
import { Table as BaseTable } from '@tiptap/extension-table';
import type { ResolvedPos } from '@tiptap/pm/model';
import { NodeSelection, Selection, TextSelection } from '@tiptap/pm/state';
import { CellSelection, findTable, isInTable, nextCell, selectedRect } from '@tiptap/pm/tables';

type TableRange = { from: number; to: number };
type VerticalDirection = 'up' | 'down';

const TABLE_CELL_MIN_WIDTH = 128;

function adjacentTable(position: ResolvedPos, direction: VerticalDirection): TableRange | null {
  const step = direction === 'down' ? 1 : -1;

  for (let depth = position.depth; depth > 0; depth -= 1) {
    const parentDepth = depth - 1;
    const parent = position.node(parentDepth);
    const siblingIndex = position.index(parentDepth) + step;
    const sibling = parent.maybeChild(siblingIndex);

    if (sibling) {
      if (sibling.type.spec.tableRole !== 'table') return null;

      const from =
        direction === 'down' ? position.after(depth) : position.before(depth) - sibling.nodeSize;
      return { from, to: from + sibling.nodeSize };
    }
  }

  return null;
}

// prosemirror-tables turns a text selection entering a table into a CellSelection and drops
// the outside anchor. Keep the text selection intact and move its head across the whole table.
function extendSelectionAcrossTable(editor: Editor, direction: VerticalDirection): boolean {
  const { selection } = editor.state;
  if (!(selection instanceof TextSelection)) return false;
  if (
    direction === 'down' ? selection.head < selection.anchor : selection.head > selection.anchor
  ) {
    return false;
  }

  const containingTable = findTable(selection.$head);
  const anchorOutsideTable = containingTable
    ? direction === 'down'
      ? selection.anchor < containingTable.pos
      : selection.anchor > containingTable.pos + containingTable.node.nodeSize
    : false;
  let tableRange =
    containingTable && anchorOutsideTable
      ? { from: containingTable.pos, to: containingTable.pos + containingTable.node.nodeSize }
      : null;
  if (!tableRange && !containingTable && editor.view.endOfTextblock(direction)) {
    tableRange = adjacentTable(selection.$head, direction);
  }
  if (!tableRange) return false;

  const step = direction === 'down' ? 1 : -1;
  const tableBoundary = direction === 'down' ? tableRange.to : tableRange.from;
  let transaction = editor.state.tr;
  let selectionOutsideTable = Selection.findFrom(
    transaction.doc.resolve(tableBoundary),
    step,
    true,
  );
  const resolvedBoundary = transaction.doc.resolve(tableBoundary);
  const adjacentNode =
    direction === 'down' ? resolvedBoundary.nodeAfter : resolvedBoundary.nodeBefore;
  if (!selectionOutsideTable && !adjacentNode) {
    const paragraph = editor.state.schema.nodes.paragraph;
    const insertionIndex = resolvedBoundary.index();
    if (
      !paragraph ||
      !resolvedBoundary.parent.canReplaceWith(insertionIndex, insertionIndex, paragraph)
    ) {
      return false;
    }

    transaction = transaction.insert(tableBoundary, paragraph.create());
    const mappedBoundary = transaction.mapping.map(tableBoundary, direction === 'down' ? -1 : 1);
    selectionOutsideTable = Selection.findFrom(transaction.doc.resolve(mappedBoundary), step, true);
  }
  if (!(selectionOutsideTable instanceof TextSelection)) return false;

  editor.view.dispatch(
    transaction
      .setSelection(
        TextSelection.create(
          transaction.doc,
          transaction.mapping.map(selection.anchor),
          selectionOutsideTable.head,
        ),
      )
      .scrollIntoView(),
  );
  return true;
}

function handleShiftArrow(editor: Editor, direction: VerticalDirection): boolean {
  const { selection } = editor.state;
  const step = direction === 'down' ? 1 : -1;
  // tableEditing leaves the outer edge unhandled, where native selection would replace the cells.
  if (selection instanceof CellSelection && !nextCell(selection.$headCell, 'vert', step)) {
    return true;
  }

  return extendSelectionAcrossTable(editor, direction);
}

// Note: a table can never be node-selected here — tableEditing (with
// allowTableNodeSelection: false) normalizes that into a CellSelection spanning
// every cell, so ancestor walks from the selection always work inside a table.
export function findTableDepth(editor: Editor): number {
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d > 0; d -= 1) {
    if ($from.node(d).type.name === 'table') return d;
  }
  return -1;
}

// toggleHeaderRow has no attribute to read back; header state IS the cell types of row one.
export function isHeaderRowActive(editor: Editor): boolean {
  const tableDepth = findTableDepth(editor);
  if (tableDepth < 0) return false;

  const table = editor.state.selection.$from.node(tableDepth);
  const firstRow = table.firstChild;
  if (!firstRow || firstRow.childCount === 0) return false;

  for (let index = 0; index < firstRow.childCount; index += 1) {
    if (firstRow.child(index).type.name !== 'tableHeader') return false;
  }

  return true;
}

// prosemirror-tables' whole-table guard runs only at dispatch time, so editor.can()
// dry-runs report true for deletions that will no-op; mirror the real guard instead.
export function canDeleteTableRow(editor: Editor): boolean {
  const { state } = editor;
  if (!isInTable(state)) return false;

  const rect = selectedRect(state);
  return !(rect.top === 0 && rect.bottom === rect.map.height);
}

export function canDeleteTableColumn(editor: Editor): boolean {
  const { state } = editor;
  if (!isInTable(state)) return false;

  const rect = selectedRect(state);
  return !(rect.left === 0 && rect.right === rect.map.width);
}

function exitTable(editor: Editor, direction: 'before' | 'after'): boolean {
  const tableDepth = findTableDepth(editor);
  if (tableDepth < 0) return false;

  const { $from } = editor.state.selection;
  const pos = direction === 'after' ? $from.after(tableDepth) : $from.before(tableDepth);
  editor
    .chain()
    .insertContentAt(pos, { type: 'paragraph' })
    .focus(pos + 1)
    .run();
  return true;
}

// True only when the caret sits on the table's outermost edge row, so intra-table
// arrow navigation still falls through to the official tableEditing plugin.
function isAtTableEdge(editor: Editor, direction: 'up' | 'down'): boolean {
  if (!editor.isActive('table')) return false;
  if (!editor.view.endOfTextblock(direction)) return false;

  const tableDepth = findTableDepth(editor);
  if (tableDepth < 0) return false;

  const { $from } = editor.state.selection;
  const table = $from.node(tableDepth);
  const rowIndex = $from.index(tableDepth);
  const isEdgeRow = direction === 'down' ? rowIndex === table.childCount - 1 : rowIndex === 0;
  if (!isEdgeRow) return false;

  const parent = $from.node(tableDepth - 1);
  const tableIndex = $from.index(tableDepth - 1);
  return direction === 'down' ? tableIndex === parent.childCount - 1 : tableIndex === 0;
}

function backspaceIntoTable(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  if (!empty || editor.isActive('table')) return false;

  const currentNode = $from.parent;
  if (currentNode.type.name !== 'paragraph' || currentNode.content.size > 0) return false;

  const paragraphBefore = $from.before($from.depth);
  const paragraphAfter = $from.after($from.depth);
  if (paragraphBefore <= 0) return false;

  const tableNode = editor.state.doc.resolve(paragraphBefore).nodeBefore;
  if (tableNode?.type.name !== 'table') return false;

  const tableStart = paragraphBefore - tableNode.nodeSize;
  const transaction = editor.state.tr.delete(paragraphBefore, paragraphAfter);
  transaction.setSelection(NodeSelection.create(transaction.doc, tableStart));
  editor.view.dispatch(transaction);
  return true;
}

export const Table = BaseTable.extend({
  addKeyboardShortcuts() {
    const parent = this.parent?.() ?? {};
    return {
      ...parent,
      'Shift-ArrowDown': ({ editor }) => handleShiftArrow(editor, 'down'),
      'Shift-ArrowUp': ({ editor }) => handleShiftArrow(editor, 'up'),
      ArrowDown: ({ editor }) =>
        isAtTableEdge(editor, 'down') ? exitTable(editor, 'after') : false,
      ArrowUp: ({ editor }) => (isAtTableEdge(editor, 'up') ? exitTable(editor, 'before') : false),
      'Mod-Enter': ({ editor }) => (editor.isActive('table') ? exitTable(editor, 'after') : false),
      Backspace: (props) =>
        backspaceIntoTable(props.editor) ||
        ((parent.Backspace as KeyboardShortcutCommand)?.(props) ?? false),
    };
  },
}).configure({
  cellMinWidth: TABLE_CELL_MIN_WIDTH,
  renderWrapper: true,
});

export { TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
